import useFeatureFlagStore from 'src/hooks/store/useFeatureFlagStore';
import { PermissionName } from '../components/permission/permissionsConstants';
/**
 * Permission check parameters interface
 */
export interface PermissionCheckParams {
  /**
   * Single or multiple permissions
   */
  permission?: PermissionName | PermissionName[];
  
  /**
   * Any one of the permissions required
   */
  anyPermission?: PermissionName[];
  
  /**
   * All permissions required
   */
  allPermissions?: PermissionName[];
  
  /**
   * Function to check single or multiple permissions
   */
  hasPermission: (permission: PermissionName | PermissionName[]) => boolean;
  
  /**
   * Function to check if user has any of the permissions
   */
  hasAnyPermission: (permissions: PermissionName[]) => boolean;
  
  /**
   * Function to check if user has all permissions
   */
  hasAllPermissions: (permissions: PermissionName[]) => boolean;
}

/**
 * Unified permission check function
 * 
 * Determines whether the user has permission to access based on the provided permission parameters and check functions
 * 
 * @param params Permission check parameters
 * @returns Whether the user has permission to access
 * 
 * @example
 * ```tsx
 * const hasAccess = checkPermission({
 *   permission: 'my_sku:read',
 *   hasPermission: (p) => store.hasPermission(p),
 *   hasAnyPermission: (p) => store.hasAnyPermission(p),
 *   hasAllPermissions: (p) => store.hasAllPermissions(p)
 * });
 * ```
 */
export const checkPermission = (params: PermissionCheckParams): boolean => {
  const {
    permission,
    anyPermission,
    allPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  } = params;

  // Check single or multiple permissions
  if (permission) {
    return hasPermission(permission);
  }

  // Check any one of the permissions
  if (anyPermission && anyPermission.length > 0) {
    return hasAnyPermission(anyPermission);
  }

  // Check all permissions
  if (allPermissions && allPermissions.length > 0) {
    return hasAllPermissions(allPermissions);
  }

  // If no permission requirements are specified, allow access by default
  return true;
};

